import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../../staff/StaffSideMenu";
import API_URL from "../../../api/API";
import { useStaffAuthContext } from "../../staff/useStaffAuthContext";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { MdAdd } from "react-icons/md";
import { CiSearch } from "react-icons/ci";
import { RiDeleteBin4Line } from "react-icons/ri";
import { FiEdit2 } from "react-icons/fi";

export const AdminManageSpeedTrainingTemplates = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate(); // Initialize navigation hook

  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // New template fields
  const [newTitle, setNewTitle] = useState("");
  const [newInfo, setNewInfo] = useState("");
  const [newStatus, setNewStatus] = useState("DRAFT");

  useEffect(() => {
    if (staff?.token) fetchTemplates();
  }, [staff?.token, currentPage]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(API_URL.ADMIN_MANAGE_SPEED_TRAINING_TEMPLATE, {
        headers: { Authorization: `Bearer ${staff.token}` },
      });
      const data = await res.json();
      setTemplates(data.data || []);
      setTotalPages(Math.ceil((data.data || []).length / itemsPerPage));
    } catch (err) {
      setError("Failed to load templates.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      const res = await fetch(API_URL.ADMIN_CREATE_SPEED_TRAINING_TEMPLATE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({
          title: newTitle,
          info: newInfo,
          status: newStatus,
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to create template.");
      }
      const newData = await res.json();
      console.log(newData.data);
      // Reset form
      setNewTitle("");
      setNewInfo("");
      setNewStatus("DRAFT");
      setCreateModalOpen(false);
      alert("New Speed Training Template Created");

      // Redirect to the edit screen for the newly created template
      if (newData?.data._id) {
        navigate(
          `/administrator/template-speed/training-template/edit/${newData.data._id}`,
        );
      } else {
        await fetchTemplates(); // ← Refresh list
      }
      //fetchTemplates(); // Refresh list
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const filteredTemplates = templates.filter((item) =>
    item.title?.toLowerCase().includes(searchQuery),
  );

  const handleDeleteTemplate = async () => {
    try {
      const res = await fetch(
        `${API_URL.ADMIN_DELETE_SPEED_TRAINING_TEMPLATE}${selectedTemplateId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        },
      );

      if (!res.ok) throw new Error("Failed to delete template.");

      setDeleteModalOpen(false);
      setSelectedTemplateId(null);
      alert("Template deleted successfully");
      fetchTemplates();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  return (
    <div className="flex md:h-screen font-inter">
      <StaffSideMenu />
      <div className="max-md:w-full md:flex-1 md:flex md:flex-col md:overflow-hidden md:overflow-y-auto bg-bgBase">
        <div className="bg-white space-y-6 pt-5 pb-3 md:py-6 px-5 md:px-10 xl:px-14">
          <div className="w-full max-w-screen-xl">
            <div className="flex flex-wrap md:justify-between md:items-center gap-3">
              <h1 className="text-2xl font-bolds">Training Programs</h1>

              <div className="flex gap-3">
                <div className="relative w-full min-w-64 max-w-64">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search templates..."
                    className="w-full p-3 border border-black/10 rounded-lg font-inter font-normal leading-3 text-sm pl-10"
                  />
                  <span className="absolute top-1/2 -translate-y-1/2 left-3">
                    <CiSearch color="#666666" size={20} />
                  </span>
                </div>
                <button
                  onClick={() => setCreateModalOpen(true)}
                  className="px-6 py-3 flex items-center gap-2 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black"
                >
                  <MdAdd color="#fff" size={18} /> Create Template
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="py-6 md:py-10 px-5 md:px-10 xl:px-14">
          <div className="w-full max-w-screen-xl">
            {error && (
              <div className="bg-red-100 text-red-500 p-3 rounded mb-4">
                {error}
              </div>
            )}

            {isLoading ? (
              <div className="bg-white border border-tblBorder p-3 rounded-10">
                <p className="text-dark font-normal text-sm">Loading...</p>
              </div>
            ) : (
              <div className="flex-1 rounded-10 bg-white border border-tblBorder overflow-auto">
                <div className="overflow-auto show-scrollbar max-h-[550px]">
                  <table className="table-auto w-full">
                    <thead className="sticky top-0 bg-white border-b border-tblBorder">
                      <tr className="text-dark font-medium text-sm">
                        <th className="px-4 md:px-6 py-2 md:py-4 text-start">
                          Title
                        </th>
                        <th className="px-4 md:px-6 py-2 md:py-4 text-start">
                          Info
                        </th>
                        <th className="px-4 md:px-6 py-2 md:py-4 text-start">
                          Status
                        </th>
                        <th className="px-4 md:px-6 py-2 md:py-4 text-end">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-tblBorder">
                      {filteredTemplates
                        .slice(
                          (currentPage - 1) * itemsPerPage,
                          currentPage * itemsPerPage,
                        )
                        .map((item, index) => (
                          <tr key={item._id} className={`cursor-pointer`}>
                            <td className="px-4 md:px-6 py-2 md:py-4">
                              <p className="text-[#111827] text-sm font-normal">
                                {item.title}
                              </p>
                            </td>
                            <td className="px-4 md:px-6 py-2 md:py-4">
                              <p className="text-slate100 text-sm font-normal">
                                {item.info}
                              </p>
                            </td>
                            <td className="px-4 md:px-6 py-2 md:py-4 capitalize">
                              <p
                                className={`py-1 px-2 text-xs font-medium leading-tight rounded-full w-fit ${
                                  item.status === "PUBLISHED"
                                    ? "bg-[#E8F5E9] text-[#2E7D32]"
                                    : "bg-[#E5E7EB] text-dark"
                                }`}
                              >
                                {item.status}
                              </p>
                            </td>
                            <td className="px-4 md:px-6 py-2 md:py-4">
                              <div className="flex justify-end items-center gap-2">
                                <Link
                                  to={`/administrator/template-speed/training-template/edit/${item._id}`}
                                  className="p-1.5 rounded hover:bg-gray-200"
                                >
                                  <FiEdit2 color="#666666" size={18} />
                                </Link>
                                <button
                                  onClick={() => {
                                    setSelectedTemplateId(item._id);
                                    setDeleteModalOpen(true);
                                  }}
                                  className="p-2 rounded-md hover:bg-gray-200"
                                >
                                  <RiDeleteBin4Line color="#666666" size={20} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-t border-tblBorder">
                  <div className="flex justify-center items-center gap-5 p-4">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                      className="p-2 text-slate100 font-medium text-sm"
                    >
                      Previous
                    </button>
                    <span className="p-2 text-dark font-medium text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                      className="p-2 text-slate100 font-medium text-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirm Modal */}
        {deleteModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
              <div className="p-5">
                <h3 className="text-dark text-xl font-medium">
                  Confirm Deletion
                </h3>
              </div>
              <div className="p-5 border-y border-tblBorder space-y-3 text-sm font-normal">
                <p>
                  Are you sure you want to delete this template?
                </p>
              </div>
              <div className="p-5">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setDeleteModalOpen(false)}
                    className="bg-transparent border border-tblBorder rounded-10 py-2.5 px-5
                         text-dark font-inter font-medium text-sm leading-tight capitalize
                          hover:bg-dark hover:text-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteTemplate}
                    className="bg-dark border border-tblBorder rounded-10 py-2.5 px-5 text-white
                         font-inter font-medium text-sm leading-tight capitalize hover:bg-black transition"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Modal */}
        {createModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
              <div className="p-5">
              <h2 className="text-dark text-xl font-medium">
                Create Speed Training Template
              </h2>
              </div>
                <div className="p-5 border-y border-tblBorder space-y-3">
                  <div>
                    <label className="text-slate100 font-normal text-xs">Title</label>
                    <input
                      type="text"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 outline-none font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-slate100 font-normal text-xs">Info</label>
                    <textarea
                      value={newInfo}
                      onChange={(e) => setNewInfo(e.target.value)}
                      className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 outline-none font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-slate100 font-normal text-xs">Status</label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 outline-none font-medium"
                    >
                      <option value="DRAFT">DRAFT</option>
                      <option value="PUBLISHED">PUBLISHED</option>
                    </select>
                  </div>
                </div>
              <div className="p-5">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setCreateModalOpen(false)}
                    className="bg-transparent border border-tblBorder rounded-10 py-2.5 px-5
                         text-dark font-inter font-medium text-sm leading-tight capitalize
                          hover:bg-dark hover:text-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateTemplate}
                    className="bg-dark border border-tblBorder rounded-10 py-2.5 px-5 text-white
                         font-inter font-medium text-sm leading-tight capitalize hover:bg-black transition"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
